import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {Calendar} from 'primereact/calendar'
import {MultiSelect} from 'primereact/multiselect'
import {InputSwitch} from 'primereact/inputswitch'
import {ProgressSpinner} from 'primereact/progressspinner'
import {Slider} from 'primereact/slider'
import {Button} from 'primereact/button'
import {OlangItem} from '../../../shared/Olang/user-interface/OlangItem/OlangItem'
// @ts-ignore - PageHeader is a JSX component
import { PageHeader } from '../../../shared/PageHeader/PageHeader'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  // Brush remplacé par composant personnalisé
} from 'recharts'
import {
  SensorData,
  DoorSensorData,
  SensorReading,
  DoorSensorReading,
} from '../../types/SensorData'
// Import pour l'API backend
import { _fetchEnginList, _getTagHistories } from '../../api/api'
// --- Préparation des données pour Recharts (plus de ChartJS) ---
import {auto} from '@popperjs/core'

type AnySensor = SensorData | DoorSensorData

type CustomLegendProps = {
  datasets: any[];
  hiddenLines: {[label: string]: boolean};
  setHiddenLines: (fn: (prev: {[label: string]: boolean}) => {[label: string]: boolean}) => void;
};

const CustomLegend: React.FC<CustomLegendProps> = ({ datasets, hiddenLines, setHiddenLines }) => {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 18,
      padding: '12px 0 6px 0',
      minHeight: 36,
      userSelect: 'none',
    }}>
      {datasets.map((ds) => (
        <div
          key={ds.label}
          onClick={() => setHiddenLines((prev) => ({ ...prev, [ds.label]: !prev[ds.label] }))}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            opacity: hiddenLines[ds.label] ? 0.4 : 1,
            fontWeight: hiddenLines[ds.label] ? 400 : 600,
            fontSize: 13,
            marginRight: 12,
            marginBottom: 3,
          }}
        >
          <span
            style={{
              width: 18,
              height: 8,
              background: ds.borderColor,
              display: 'inline-block',
              borderRadius: 3,
              marginRight: 8,
              border: '1px solid #bbb',
              boxShadow: hiddenLines[ds.label] ? 'none' : '0 0 0 2px #fff',
              transition: 'opacity 0.2s',
            }}
          />
          {ds.label}
        </div>
      ))}
    </div>
  );
};

// Composant de loader moderne avec PrimeFlex
const ChartLoader: React.FC = () => {
  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black-alpha-30 flex align-items-center justify-content-center" 
         style={{ zIndex: 9999 }}>
      <div className="bg-white border-round-xl p-6 shadow-8 flex flex-column align-items-center justify-content-center"
           style={{ minWidth: '320px', maxWidth: '400px' }}>
        
        {/* Header avec icône */}
        <div className="flex align-items-center justify-content-center mb-4">
          <div className="bg-blue-50 border-circle p-3 mr-3">
            <i className="pi pi-chart-line text-blue-500 text-2xl"></i>
          </div>
          <div>
            <h3 className="text-900 font-semibold m-0 text-lg">
              Chargement des Courbes
            </h3>
            <p className="text-600 m-0 text-sm">
              Récupération des données historiques
            </p>
          </div>
        </div>
        
        {/* Spinner principal */}
        <div className="flex align-items-center justify-content-center mb-4">
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3" 
            fill="transparent" 
            animationDuration="1.2s"
          />
        </div>
        
        {/* Barre de progression stylée */}
        <div className="w-full mb-3">
          <div className="bg-blue-50 border-round h-1rem overflow-hidden">
            <div className="bg-blue-500 h-full border-round animate-pulse" 
                 style={{ width: '60%', transition: 'width 0.3s ease' }}></div>
          </div>
        </div>
        
        {/* Messages informatifs */}
        <div className="text-center">
          <p className="text-700 font-medium m-0 mb-2">
            Traitement en cours...
          </p>
          <p className="text-500 text-sm m-0">
            <i className="pi pi-clock mr-1"></i>
            Cela peut prendre quelques secondes
          </p>
        </div>
      </div>
    </div>
  );
};

// Loader inline pour les sections spécifiques
const InlineLoader: React.FC<{ message?: string }> = ({ message = "Chargement..." }) => {
  return (
    <div className="flex flex-column align-items-center justify-content-center p-6 bg-blue-50 border-round-lg">
      <ProgressSpinner 
        style={{ width: '32px', height: '32px' }} 
        strokeWidth="4" 
        fill="transparent" 
        animationDuration="1s"
      />
      <p className="text-600 mt-3 mb-0 font-medium">
        <i className="pi pi-spin pi-spinner mr-2"></i>
        {message}
      </p>
    </div>
  );
};

// Loader compact pour les boutons ou petites sections
const CompactLoader: React.FC<{ size?: 'small' | 'medium' | 'large' }> = ({ size = 'medium' }) => {
  const sizeMap = {
    small: { width: '16px', height: '16px' },
    medium: { width: '24px', height: '24px' },
    large: { width: '32px', height: '32px' }
  };
  
  return (
    <div className="flex align-items-center justify-content-center">
      <ProgressSpinner 
        style={sizeMap[size]} 
        strokeWidth="3" 
        fill="transparent" 
        animationDuration="1s"
      />
    </div>
  );
};

// Composant pour l'état vide avec PrimeFlex
const EmptyState: React.FC = () => {
  return (
    <div className="flex flex-column align-items-center justify-content-center p-8 bg-blue-50 border-round-lg border-2 border-dashed surface-border" 
         style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="bg-blue-100 border-circle p-4 inline-block mb-4">
          <i className="pi pi-chart-line text-blue-500 text-5xl"></i>
        </div>
        
        <h3 className="text-900 font-semibold mb-3 text-xl">
          Aucune donnée à afficher
        </h3>
        
        <p className="text-600 mb-4 line-height-3" style={{ maxWidth: '400px' }}>
          Sélectionnez des capteurs et définissez une période pour visualiser 
          les courbes de température et d'humidité
        </p>
        
        <div className="bg-white border-round p-4 shadow-2">
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center">
              <i className="pi pi-check-circle text-green-500 mr-2"></i>
              <span className="text-700 text-sm">Sélectionnez un ou plusieurs capteurs</span>
            </div>
            <div className="flex align-items-center">
              <i className="pi pi-check-circle text-green-500 mr-2"></i>
              <span className="text-700 text-sm">Définissez la période d'analyse</span>
            </div>
            <div className="flex align-items-center">
              <i className="pi pi-check-circle text-green-500 mr-2"></i>
              <span className="text-700 text-sm">Les courbes s'afficheront automatiquement</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant de navigation temporelle ultra-fluide
const TimeNavigator: React.FC<{
  totalPoints: number;
  timeRange: [number, number];
  onRangeChange: (range: [number, number]) => void;
  timestamps: string[];
}> = ({ totalPoints, timeRange, onRangeChange, timestamps }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  // Calcul des indices basés sur le pourcentage
  const startIndex = Math.floor((timeRange[0] / 100) * (totalPoints - 1));
  const endIndex = Math.floor((timeRange[1] / 100) * (totalPoints - 1));
  
  // Formatage des dates pour l'affichage
  const formatDate = (index: number) => {
    if (!timestamps[index]) return '';
    return new Date(timestamps[index]).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calcul de la durée sélectionnée
  const selectedDuration = endIndex - startIndex;
  const selectionPercentage = Math.round(((endIndex - startIndex) / totalPoints) * 100);
  
  return (
    <div className="bg-white border-round-lg shadow-3 p-4 mt-4" style={{ minWidth: '900px', margin: '0 auto' }}>
      {/* Header avec informations */}
      <div className="flex align-items-center justify-content-between mb-3">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-clock text-blue-500 text-xl"></i>
          <h3 className="text-900 font-semibold m-0">Navigation Temporelle</h3>
        </div>
        
        <div className="flex align-items-center gap-3">
          <span className="text-500 text-sm">
            {totalPoints} points de données
          </span>
          <span className="bg-blue-100 text-blue-700 px-3 py-1 border-round font-medium text-sm">
            {selectionPercentage}% sélectionné
          </span>
        </div>
      </div>
      
      {/* Informations de période sélectionnée */}
      <div className="bg-blue-50 border-round p-3 mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-calendar text-green-600"></i>
              <div>
                <span className="text-600 text-sm block">Début de période</span>
                <span className="text-900 font-medium">{formatDate(startIndex)}</span>
              </div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-calendar text-red-600"></i>
              <div>
                <span className="text-600 text-sm block">Fin de période</span>
                <span className="text-900 font-medium">{formatDate(endIndex)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Slider ultra-fluide */}
      <div className="mb-4">
        <div className="flex align-items-center gap-3 mb-2">
          <span className="text-700 font-medium text-sm">Sélection de période :</span>
          <span className="text-500 text-sm">
            {selectedDuration + 1} points ({selectionPercentage}% du total)
          </span>
        </div>
        
        <div className="relative">
          {/* Barre de progression visuelle */}
          <div className="bg-blue-100 border-round h-2rem mb-2 relative overflow-hidden">
            <div 
              className="bg-blue-500 h-full transition-all duration-200 ease-out"
              style={{
                marginLeft: `${timeRange[0]}%`,
                width: `${timeRange[1] - timeRange[0]}%`,
                opacity: isDragging ? 0.8 : 0.6
              }}
            ></div>
            
            {/* Indicateurs de position */}
            <div 
              className="absolute top-0 w-1 bg-green-500 h-full transition-all duration-200"
              style={{ left: `${timeRange[0]}%` }}
            ></div>
            <div 
              className="absolute top-0 w-1 bg-red-500 h-full transition-all duration-200"
              style={{ left: `${timeRange[1]}%` }}
            ></div>
          </div>
          
          {/* Slider range PrimeReact ultra-réactif */}
          <Slider
            value={timeRange}
            onChange={(e) => {
              setIsDragging(true);
              onRangeChange(e.value as [number, number]);
              setTimeout(() => setIsDragging(false), 100);
            }}
            range
            min={0}
            max={100}
            step={0.1}
            className="w-full"
            style={{ height: '6px' }}
          />
        </div>
      </div>
      
      {/* Boutons de navigation rapide */}
      <div className="flex align-items-center justify-content-center gap-2 flex-wrap">
        <Button
          label="Tout"
          icon="pi pi-expand"
          size="small"
          outlined
          onClick={() => onRangeChange([0, 100])}
          className="text-xs"
        />
        
        <Button
          label="Première moitié"
          icon="pi pi-step-backward"
          size="small"
          outlined
          onClick={() => onRangeChange([0, 50])}
          className="text-xs"
        />
        
        <Button
          label="Centre"
          icon="pi pi-circle"
          size="small"
          outlined
          onClick={() => onRangeChange([25, 75])}
          className="text-xs"
        />
        
        <Button
          label="Dernière moitié"
          icon="pi pi-step-forward"
          size="small"
          outlined
          onClick={() => onRangeChange([50, 100])}
          className="text-xs"
        />
        
        <Button
          label="Zoom +"
          icon="pi pi-search-plus"
          size="small"
          outlined
          onClick={() => {
            const center = (timeRange[0] + timeRange[1]) / 2;
            const currentWidth = timeRange[1] - timeRange[0];
            const newWidth = Math.max(5, currentWidth * 0.7);
            const newStart = Math.max(0, center - newWidth / 2);
            const newEnd = Math.min(100, center + newWidth / 2);
            onRangeChange([newStart, newEnd]);
          }}
          className="text-xs"
        />
        
        <Button
          label="Zoom -"
          icon="pi pi-search-minus"
          size="small"
          outlined
          onClick={() => {
            const center = (timeRange[0] + timeRange[1]) / 2;
            const currentWidth = timeRange[1] - timeRange[0];
            const newWidth = Math.min(100, currentWidth * 1.3);
            const newStart = Math.max(0, center - newWidth / 2);
            const newEnd = Math.min(100, center + newWidth / 2);
            onRangeChange([newStart, newEnd]);
          }}
          className="text-xs"
        />
      </div>
    </div>
  );
};

// Composant pour l'état de chargement des données
const DataLoadingState: React.FC = () => {
  return (
    <div className="flex flex-column align-items-center justify-content-center p-8 bg-white border-round-lg shadow-2" 
         style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="bg-orange-50 border-circle p-4 inline-block mb-4">
          <i className="pi pi-sync pi-spin text-orange-500 text-4xl"></i>
        </div>
        
        <h3 className="text-900 font-semibold mb-3">
          Chargement des données historiques
        </h3>
        
        <p className="text-600 mb-4">
          Récupération des mesures de capteurs...
        </p>
        
        <InlineLoader message="Traitement en cours" />
        
        <div className="mt-4 p-3 bg-orange-50 border-round">
          <div className="flex align-items-center justify-content-center">
            <i className="pi pi-info-circle text-orange-500 mr-2"></i>
            <span className="text-600 text-sm">Cela peut prendre quelques instants</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Courbe() {
  // State pour les capteurs chargés depuis le backend
  const [capteurs, setCapteurs] = useState<SensorData[]>([])
  const [loadingCapteurs, setLoadingCapteurs] = useState<boolean>(true)
  
  // Utiliser seulement les capteurs chargés depuis l'API
  const allSensors: AnySensor[] = [...capteurs]
  const [selectedSensors, setSelectedSensors] = useState<AnySensor[]>([])
  
  // State séparé pour les historiques des capteurs sélectionnés
  const [sensorHistories, setSensorHistories] = useState<{[macAddr: string]: SensorReading[]}>({})
  
  // State pour suivre les capteurs dont on a déjà chargé l'historique
  const [loadedSensors, setLoadedSensors] = useState<string[]>([])
  
  // Initialiser avec les dernières 24h par défaut
  const getDefault24HourRange = () => {
    const now = new Date()
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    return { start: yesterday, end: now }
  }
  
  const [startDate, setStartDate] = useState<Date | null>(() => getDefault24HourRange().start)
  const [endDate, setEndDate] = useState<Date | null>(() => getDefault24HourRange().end)
  
  // State pour savoir si l'utilisateur a sélectionné une période personnalisée
  const [isCustomPeriod, setIsCustomPeriod] = useState<boolean>(false)
  
  // State pour la navigation par jour (utilisé quand pas de période personnalisée)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  // State pour toggle l'affichage des courbes
  const [hiddenLines, setHiddenLines] = useState<{[label: string]: boolean}>({});
  // State pour la navigation temporelle personnalisée
  const [timeRange, setTimeRange] = useState<[number, number]>([0, 100]);
  // State pour le debouncing de la navigation
  const [navDebounceTimeout, setNavDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  // State pour l'affichage des détails de la période
  const [showTimeDetails, setShowTimeDetails] = useState<boolean>(false);
  // State pour contrôler l'affichage des légendes (masquées par défaut)
  const [showLegends, setShowLegends] = useState<boolean>(false);
  // State pour le loader
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Fonctions de navigation par jour
  const goToPreviousDay = () => {
    if (!isCustomPeriod) {
      const prevDay = new Date(selectedDate)
      prevDay.setDate(prevDay.getDate() - 1)
      setSelectedDate(prevDay)
      
      // Mettre à jour les dates pour les dernières 24h du jour précédent
      const endOfDay = new Date(prevDay)
      endOfDay.setHours(23, 59, 59, 999)
      const startOfDay = new Date(prevDay)
      startOfDay.setHours(0, 0, 0, 0)
      
      setStartDate(startOfDay)
      setEndDate(endOfDay)
    }
  }

  const goToNextDay = () => {
    if (!isCustomPeriod) {
      const nextDay = new Date(selectedDate)
      nextDay.setDate(nextDay.getDate() + 1)
      setSelectedDate(nextDay)
      
      // Mettre à jour les dates pour les dernières 24h du jour suivant
      const endOfDay = new Date(nextDay)
      endOfDay.setHours(23, 59, 59, 999)
      const startOfDay = new Date(nextDay)
      startOfDay.setHours(0, 0, 0, 0)
      
      setStartDate(startOfDay)
      setEndDate(endOfDay)
    }
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isFutureDate = (date: Date) => {
    const today = new Date()
    return date > today
  }

  // Gestionnaires pour les sélecteurs de dates personnalisées
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date)
    setIsCustomPeriod(true)
  }

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date)
    setIsCustomPeriod(true)
  }

  // Fonction pour revenir au mode 24h par défaut
  const resetTo24HourMode = () => {
    const range = getDefault24HourRange()
    setStartDate(range.start)
    setEndDate(range.end)
    setSelectedDate(new Date())
    setIsCustomPeriod(false)
  }

  // Fonction pour charger les capteurs depuis le backend (engin/list)
  const loadCapteurs = async () => {
    try {
      setLoadingCapteurs(true)
      console.log('📊 Chargement des capteurs pour Courbe...')
      
      const response = await _fetchEnginList()
      console.log('📊 Réponse API engins:', response)
      
      if (response.data && response.data.success && response.data.result) {
        const enginsData = Array.isArray(response.data.result) ? response.data.result : []
        console.log('📊 Engins data received:', enginsData.length)
        
        // Transformer les données engin en format SensorData avec gestion des capteurs de porte
        const transformedSensors: (SensorData | DoorSensorData)[] = enginsData
          .filter((engin: any) => engin.temperature !== null || engin.humidity !== null) // Garder seulement les engins avec temp/humidity
          .map((engin: any) => {
            // Données de base communes
            const baseData = {
              macAddr: engin.tagname || engin.macAddr || engin.uid || 'N/A',
              label: engin.title || engin.label || engin.reference || `Engin-${engin.uid}`,
              rssi: engin.rssi || 0,
              battery: parseInt(engin.batteries) || 0,
              alertDelay: 30,
              lastSeen: engin.lastSeenAt || engin.tagDate || new Date().toISOString(),
              alertes: false,
              rapports: true,
              actif: engin.sysActive === 1,
              historique: [] as SensorReading[]
            }

            // Si door est null, c'est un capteur normal (température/humidité seulement)
            if (engin.door === null) {
              return {
                ...baseData,
                temperature: engin.temperature || 0,
                humidity: engin.humidity || 0,
                temperatureMin: engin.temperatureThresholdMin || engin.temperatureMin || -5,
                temperatureMax: engin.temperatureThresholdMax || engin.temperatureMax || 25,
                humidityMin: engin.humidityThresholdMin || engin.humidityMin || 30,
                humidityMax: engin.humidityThresholdMax || engin.humidityMax || 70,
                // Champs engin spécifiques
                tagname: engin.tagname,
                uid: engin.uid,
                id: engin.id,
                title: engin.title,
                reference: engin.reference,
                name: engin.title || engin.label
              } as SensorData
            } else {
              // Si door est 0 ou 1, c'est un capteur avec données de porte
              return {
                ...baseData,
                temperature: engin.temperature || 0,
                humidity: engin.humidity || 0,
                ouvert: engin.door === 1,
                temperatureMin: engin.temperatureThresholdMin || engin.temperatureMin || -5,
                temperatureMax: engin.temperatureThresholdMax || engin.temperatureMax || 25,
                humidityMin: engin.humidityThresholdMin || engin.humidityMin || 30,
                humidityMax: engin.humidityThresholdMax || engin.humidityMax || 70,
                // Champs engin spécifiques
                tagname: engin.tagname,
                uid: engin.uid,
                id: engin.id,
                title: engin.title,
                reference: engin.reference,
                name: engin.title || engin.label,
                historique: [] as DoorSensorReading[]
              } as DoorSensorData
            }
          });
        
        setCapteurs(transformedSensors as SensorData[])
        console.log('✅ Capteurs chargés pour Courbe:', transformedSensors.length)
        console.log('📊 Capteurs avec porte:', transformedSensors.filter(s => 'ouvert' in s).length)
      } else {
        console.log('❌ Aucun capteur reçu')
        setCapteurs([])
      }
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des capteurs:', error)
      setCapteurs([])
    } finally {
      setLoadingCapteurs(false)
    }
  }

  // Fonction pour récupérer l'historique des capteurs sélectionnés
  const loadSensorHistories = async (sensors: AnySensor[]) => {
    if (sensors.length === 0 || !startDate || !endDate) {
      return
    }

    try {
      console.log('📊 Chargement des historiques pour les capteurs sélectionnés:', sensors.length)
      
      // Préparer les requêtes pour tous les capteurs sélectionnés
      const historyPromises = sensors.map(async (sensor) => {
        try {
          // Extraire l'ID approprié selon le type de capteur
          const getSensorId = (s: AnySensor): string => {
            if ('uid' in s && s.uid) return s.uid;
            if ('id' in s && s.id) return s.id;
            return s.macAddr; // Fallback sur macAddr
          };

          const response = await _getTagHistories({
            tags: [], // Vide pour les engins
            srcIds: [getSensorId(sensor)], // Utiliser uid, id ou macAddr
            from: startDate.toISOString(),
            to: endDate.toISOString(),
            srcObject: 'engin' // Spécifier que c'est un engin
          })
          
          if (response.success && response.response && Array.isArray(response.response)) {
            // Regrouper les données par timestamp pour combiner temp et hum sur la même ligne
            const groupedData: { [key: string]: { timestamp: string, temperature: number, humidity: number } } = {}
            
            response.response.forEach((item: any) => {
              const timestamp = item.datetimezone || item.timestamp || new Date().toISOString()
              
              if (!groupedData[timestamp]) {
                groupedData[timestamp] = {
                  timestamp,
                  temperature: 0,
                  humidity: 0
                }
              }
              
              if (item.type === 'temperature') {
                groupedData[timestamp].temperature = parseFloat((item.mean || item.value || 0).toFixed(2))
              } else if (item.type === 'humidity' || item.type === 'humidity') {
                groupedData[timestamp].humidity = parseFloat((item.mean || item.value || 0).toFixed(2))
              }
            })
            
            // Convertir en tableau et trier par timestamp
            const readings: SensorReading[] = Object.values(groupedData)
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
            
            return { sensor, readings }
          }
        } catch (error) {
          console.error(`🚨 Erreur lors du chargement de l'historique pour ${sensor.label}:`, error)
        }
        return { sensor, readings: [] }
      })
      
      // Attendre toutes les requêtes
      const results = await Promise.all(historyPromises)
      
      // Mettre à jour le state des historiques
      const newHistories: {[macAddr: string]: SensorReading[]} = {}
      results.forEach(result => {
        if (result && result.sensor && result.readings) {
          newHistories[result.sensor.macAddr] = result.readings
        }
      })
      
      setSensorHistories(prev => ({ ...prev, ...newHistories }))
      console.log('✅ Historiques chargés pour tous les capteurs sélectionnés')
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des historiques:', error)
    }
  }

  // Effect pour charger les historiques quand les capteurs sont sélectionnés ou les dates changent
  useEffect(() => {
    if (selectedSensors.length === 0 || !startDate || !endDate) {
      setIsLoading(false)
      return
    }

    // Créer une clé unique pour cette combinaison de capteurs et dates
    const dateKey = `${startDate.toISOString()}-${endDate.toISOString()}`
    const sensorsKey = selectedSensors.map(s => s.macAddr).sort().join(',')
    const loadKey = `${sensorsKey}-${dateKey}`
    
    // Ne charger que si cette combinaison n'a pas déjà été chargée
    if (!loadedSensors.includes(loadKey)) {
      setIsLoading(true)
      
      loadSensorHistories(selectedSensors).finally(() => {
        setIsLoading(false)
        setLoadedSensors(prev => [...prev, loadKey])
      })
    }
  }, [selectedSensors, startDate, endDate, loadedSensors])

  // Effect pour nettoyer le cache des capteurs chargés quand les dates changent
  useEffect(() => {
    // Vider le cache quand les dates changent pour forcer le rechargement
    setLoadedSensors([])
    setSensorHistories({})
  }, [startDate, endDate])

  // Charger les capteurs au montage du composant
  useEffect(() => {
    loadCapteurs()
  }, [])
  
  // Filtre les historiques par date pour chaque capteur (température/humidité ou porte)
  const getFilteredReadings = (sensor: AnySensor): (SensorReading | DoorSensorReading)[] => {
    if ('temperature' in sensor) {
      // SensorData - utiliser sensorHistories au lieu de sensor.historique
      const sensorHistory = sensorHistories[sensor.macAddr] || []
      return sensorHistory.filter((r: SensorReading) => {
        const d = new Date(r.timestamp).getTime()
        return (!startDate || d >= startDate.getTime()) && (!endDate || d <= endDate.getTime())
      })
    } else {
      // DoorSensorData - utiliser l'historique du capteur directement
      if (
        'historique' in sensor &&
        Array.isArray(sensor.historique) &&
        sensor.historique.length > 0
      ) {
        return (sensor.historique as DoorSensorReading[]).filter((r: DoorSensorReading) => {
          const d = new Date(r.timestamp).getTime()
          return (!startDate || d >= startDate.getTime()) && (!endDate || d <= endDate.getTime())
        })
      }
    }
    return []
  }

  // Récupère tous les timestamps uniques des historiques filtrés (avec heure précise)
  const allTimestamps = Array.from(
    new Set(
      selectedSensors.flatMap((sensor) =>
        getFilteredReadings(sensor).map((r: SensorReading | DoorSensorReading) =>
          r.timestamp
        )
      )
    )
  ).sort()

  // Fonction optimisée pour la navigation temporelle avec debouncing
  const handleTimeRangeChange = useCallback((newRange: [number, number]) => {
    // Nettoyer le timeout précédent
    if (navDebounceTimeout) {
      clearTimeout(navDebounceTimeout)
    }
    
    // Appliquer immédiatement pour la fluidité visuelle
    setTimeRange(newRange)
    
    // Debouncer les recalculs coûteux
    const newTimeout = setTimeout(() => {
      console.log('🎯 Navigation temporelle finalisée:', {
        range: newRange
      })
    }, 100) // 100ms de debounce pour plus de réactivité
    
    setNavDebounceTimeout(newTimeout)
  }, [navDebounceTimeout])
  
  // Nettoyage du timeout au démontage
  useEffect(() => {
    return () => {
      if (navDebounceTimeout) {
        clearTimeout(navDebounceTimeout)
      }
    }
  }, [navDebounceTimeout])
  
  // Calcul des indices de filtrage basés sur la navigation temporelle
  const getFilteredIndices = useMemo(() => {
    const totalPoints = allTimestamps.length
    if (totalPoints === 0) return { startIndex: 0, endIndex: 0 }
    
    const startIndex = Math.floor((timeRange[0] / 100) * (totalPoints - 1))
    const endIndex = Math.floor((timeRange[1] / 100) * (totalPoints - 1))
    
    return { startIndex, endIndex }
  }, [timeRange, allTimestamps])

  // Génère une palette de couleurs
  const colors = [
    '#f87171',
    '#60a5fa',
    '#fbbf24',
    '#34d399',
    '#a78bfa',
    '#f472b6',
    '#38bdf8',
    '#facc15',
    '#10b981',
    '#6366f1',
    '#e879f9',
    '#fb7185',
  ]

  // Prépare les datasets pour chaque type de donnée
  const temperatureDatasets = selectedSensors
    .filter((s) => 'temperature' in s)
    .map((sensor: AnySensor, idx) => {
      const readingsByTimestamp: {[timestamp: string]: number} = {}
      getFilteredReadings(sensor).forEach((r: any) => {
        readingsByTimestamp[r.timestamp] = r.temperature
      })
      return {
        label: `${sensor.label} (°C)`,
        data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp] ?? null),
        borderColor: colors[idx % colors.length],
        backgroundColor: colors[idx % colors.length],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
      }
    })

  const humidityDatasets = selectedSensors
    .filter((s) => 'humidity' in s)
    .map((sensor: AnySensor, idx) => {
      const readingsByTimestamp: {[timestamp: string]: number} = {}
      getFilteredReadings(sensor).forEach((r: any) => {
        readingsByTimestamp[r.timestamp] = r.humidity
      })
      return {
        label: `${sensor.label} (%)`,
        data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp] ?? null),
        borderColor: colors[(idx + 4) % colors.length],
        backgroundColor: colors[(idx + 4) % colors.length],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
      }
    })

  const doorDatasets = selectedSensors
    .filter((s) => 'ouvert' in s)
    .map((sensor: AnySensor, idx) => {
      const readingsByTimestamp: {[timestamp: string]: number} = {}
      getFilteredReadings(sensor).forEach((r: any) => {
        readingsByTimestamp[r.timestamp] = r.ouvert ? 1 : 0
      })
      return {
        label: `${sensor.label} (porte)`,
        data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp] ?? null),
        borderColor: colors[(idx + 8) % colors.length],
        backgroundColor: colors[(idx + 8) % colors.length],
        fill: false,
        tension: 0.1,
        pointRadius: 2,
        stepped: true,
      }
    })

  // --- Préparation des datasets pour Recharts ---
  // On prépare les données pour les 3 graphiques : température, humidité, porte

  // Température
  const temperatureChartData = {
    labels: allTimestamps.map((timestamp: string) => {
      const d = new Date(timestamp)
      return d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }),
    datasets: selectedSensors
      .filter((s) => 'temperature' in s)
      .flatMap((sensor: any, idx) => {
        const readingsByTimestamp: {[timestamp: string]: any} = {}
        getFilteredReadings(sensor).forEach((r: any) => {
          readingsByTimestamp[r.timestamp] = r
        })
        const tempColor = colors[(idx * 2) % colors.length]
        const humColor = colors[(idx * 2 + 1) % colors.length]
        const arr: any[] = [
          {
            label: `${sensor.label} - Température (°C)`,
            data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp]?.temperature ?? null),
            borderColor: tempColor,
          },
          {
            label: `${sensor.label} - Humidité (%)`,
            data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp]?.humidity ?? null),
            borderColor: humColor,
          },
        ]
        if ('temperatureMin' in sensor) {
          arr.push({
            label: `${sensor.label} - Température Min`,
            data: allTimestamps.map((_timestamp: string) => sensor.temperatureMin),
            borderColor: tempColor,
          })
        }
        if ('temperatureMax' in sensor) {
          arr.push({
            label: `${sensor.label} - Température Max`,
            data: allTimestamps.map((_timestamp: string) => sensor.temperatureMax),
            borderColor: tempColor,
          })
        }
        return arr
      }),
  }

  // Humidité
  const humidityChartData = {
    labels: temperatureChartData.labels,
    datasets: selectedSensors
      .filter((s) => 'humidity' in s)
      .flatMap((sensor: any, idx) => {
        const readingsByTimestamp: {[timestamp: string]: any} = {}
        getFilteredReadings(sensor).forEach((r: any) => {
          readingsByTimestamp[r.timestamp] = r
        })
        const humColor = colors[(idx * 2 + 1) % colors.length]
        const arr: any[] = [
          {
            label: `${sensor.label} - Humidité (%)`,
            data: allTimestamps.map((timestamp: string) => readingsByTimestamp[timestamp]?.humidity ?? null),
            borderColor: humColor,
          },
        ]
        if ('humidityMin' in sensor) {
          arr.push({
            label: `${sensor.label} - Humidité Min`,
            data: allTimestamps.map((_timestamp: string) => sensor.humidityMin),
            borderColor: humColor,
          })
        }
        if ('humidityMax' in sensor) {
          arr.push({
            label: `${sensor.label} - Humidité Max`,
            data: allTimestamps.map((_timestamp: string) => sensor.humidityMax),
            borderColor: humColor,
          })
        }
        return arr
      }),
  }

  // Porte
  const doorChartData = {
    labels: temperatureChartData.labels,
    datasets: selectedSensors
      .filter((s) => 'ouvert' in s)
      .map((sensor: any, idx) => {
        const readingsByTimestamp: {[timestamp: string]: any} = {}
        getFilteredReadings(sensor).forEach((r: any) => {
          readingsByTimestamp[r.timestamp] = r
        })
        const color = colors[(idx + 8) % colors.length]
        return {
          label: `${sensor.label} (porte)`,
          data: allTimestamps.map((timestamp) => {
            const reading = readingsByTimestamp[timestamp]
            // Si ouvert est 0 (fermé), traiter comme si aucune donnée n'était reçue
            if (reading && reading.ouvert === false) {
              return null
            }
            return reading?.ouvert ? 1 : null
          }),
          borderColor: color,
        }
      }),
  }

  // Initialisation de la navigation temporelle lors du chargement des données
  useEffect(() => {
    if (allTimestamps.length > 0 && timeRange[0] === 0 && timeRange[1] === 100) {
      // Garder la vue complète par défaut
      console.log(`📊 Navigation temporelle initialisée avec ${allTimestamps.length} points de données`)
    }
  }, [allTimestamps.length, timeRange])
  
  // --- Les données sont utilisées directement dans les composants Recharts plus bas ---

  // Afficher un loader moderne pendant le chargement des capteurs
  if (loadingCapteurs) {
    return (
      <div className='p-6 min-h-screen surface-ground'>
        <h1 className='text-3xl font-bold mb-8 text-900 text-center'>
          <i className="pi pi-chart-line mr-3 text-blue-500"></i>
          Visualisation des Courbes
        </h1>
        
        {/* Loader moderne pour le chargement initial */}
        <div className='flex justify-content-center mt-8'>
          <div className='bg-white border-round-xl p-6 shadow-4 text-center' style={{ maxWidth: '400px' }}>
            <div className='bg-blue-50 border-circle p-4 inline-block mb-4'>
              <i className='pi pi-database text-blue-500 text-3xl'></i>
            </div>
            
            <h3 className='text-900 font-semibold mb-3'>Initialisation</h3>
            <p className='text-600 mb-4'>Chargement de la liste des capteurs disponibles</p>
            
            <div className='flex align-items-center justify-content-center mb-4'>
              <ProgressSpinner 
                style={{ width: '40px', height: '40px' }} 
                strokeWidth='3' 
                fill='transparent' 
                animationDuration='1.5s'
              />
            </div>
            
            <div className='bg-blue-50 border-round p-3'>
              <div className='flex align-items-center justify-content-center'>
                <i className='pi pi-info-circle text-blue-500 mr-2'></i>
                <span className='text-600 text-sm'>Récupération des données depuis le serveur</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6 min-h-screen surface-ground relative'>
      {/* Loader overlay moderne */}
      {isLoading && <ChartLoader />}
      
      {/* Page Header */}
      {/* @ts-ignore */}
      <PageHeader
        titleOlang="curves.visualization"
        backPath="/sensors"
        info={
          <div className="flex align-items-center gap-3">
            {selectedSensors.length > 0 && (
              <div className="flex align-items-center gap-2">
                <i className="pi pi-microchip text-xl text-primary"></i>
                <span className="text-lg font-semibold">
                  {selectedSensors.length} capteur{selectedSensors.length > 1 ? 's' : ''} sélectionné{selectedSensors.length > 1 ? 's' : ''}
                </span>
              </div>
            )}
            {temperatureChartData.labels.length > 0 && (
              <>
                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-chart-line text-xl text-primary"></i>
                  <span className="text-lg font-semibold">
                    {temperatureChartData.labels.length} point{temperatureChartData.labels.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        }
        className="mb-4"
      />

      {/* Sélection des capteurs - Mise en avant */}
      <div className="surface-card border-round-lg shadow-4 p-4 mb-4 border-2 border-primary-200">
        <div className="flex align-items-center gap-3 mb-3">
          <div className="bg-primary-50 border-circle p-3">
            <i className="pi pi-microchip text-primary text-2xl"></i>
          </div>
          <div>
            <h3 className="text-900 font-bold text-2xl m-0">Choisir les capteurs</h3>
            <p className="text-600 m-0 font-medium">Sélectionnez un ou plusieurs capteurs pour visualiser leurs courbes</p>
          </div>
        </div>
        <MultiSelect
          value={selectedSensors}
          onChange={(e) => setSelectedSensors(e.value)}
          options={allSensors}
          optionLabel='label'
          filter
          display='chip'
          placeholder='🔍 Sélectionner un ou plusieurs capteurs (température, humidité ou porte)...'
          className='w-full'
          style={{minHeight: '3.5rem', fontSize: '1.1rem', maxHeight: 120, overflowY: 'auto', flexWrap: 'wrap'}}
          filterPlaceholder="Rechercher un capteur..."
        />
        {selectedSensors.length === 0 && (
          <div className="mt-3 p-3 bg-orange-50 border-round border-1 border-orange-200">
            <div className="flex align-items-center">
              <i className="pi pi-info-circle text-orange-500 mr-2"></i>
              <span className="text-orange-700 text-sm font-medium">
                Veuillez sélectionner au moins un capteur pour afficher les courbes
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Filtres de période - Affichés seulement si des capteurs sont sélectionnés */}
      {selectedSensors.length > 0 && (
        <div className="surface-100 border-round p-4 mb-4">
          <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-calendar text-600"></i>
            <h4 className="text-900 font-semibold text-lg m-0">Période d'analyse</h4>
          </div>

          {/* Navigation par jour (affichée seulement si pas de période personnalisée) */}
          {!isCustomPeriod && (
            <div className="flex align-items-center justify-content-between mb-4 p-3 bg-blue-50 border-round border-1 border-blue-200">
              <Button
                icon="pi pi-chevron-left"
                label="Jour précédent"
                onClick={goToPreviousDay}
                className="p-button-outlined p-button-sm"
                size="small"
              />
              
              <div className="text-center">
                <div className="text-900 font-semibold text-lg">
                  {selectedDate.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  {isToday(selectedDate) && (
                    <span className="ml-2 text-sm text-green-600 font-normal">
                      (Aujourd'hui)
                    </span>
                  )}
                </div>
              </div>
              
              <Button
                icon="pi pi-chevron-right"
                label="Jour suivant"
                iconPos="right"
                onClick={goToNextDay}
                disabled={isFutureDate(selectedDate)}
                className="p-button-outlined p-button-sm"
                size="small"
              />
            </div>
          )}

          <div className='w-full flex flex-col md:flex-row items-center gap-4 mb-6 justify-content-between'>
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <Calendar
                value={startDate}
                onChange={(e) => handleStartDateChange(e.value as Date)}
                placeholder='Date et heure de début'
                className='w-full md:w-auto'
                showIcon
                showTime
                hourFormat='24'
                stepMinute={15}
              />
              <Calendar
                value={endDate}
                onChange={(e) => handleEndDateChange(e.value as Date)}
                placeholder='Date et heure de fin'
                className='w-full md:w-auto'
                showIcon
                showTime
                hourFormat='24'
                stepMinute={15}
              />
              {/* Bouton pour revenir au mode 24h (affiché seulement en mode période personnalisée) */}
              {isCustomPeriod && (
                <Button
                  icon="pi pi-refresh"
                  label="Mode 24h"
                  onClick={resetTo24HourMode}
                  className="p-button-success p-button-outlined"
                  size="small"
                  tooltip="Revenir au mode 24h avec navigation par jour"
                />
              )}
            </div>
            {/* Toggle pour les légendes */}
            <div className='flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg shadow-sm border border-blue-200'>
              <i className='pi pi-eye text-blue-600' />
              <span className='text-sm font-medium text-blue-800'>Légendes</span>
              <InputSwitch
                checked={showLegends}
                onChange={(e) => setShowLegends(e.value || false)}
                className='ml-1'
              />
            </div>
          </div>
        </div>
      )}

      {/* Affichage conditionnel : EmptyState ou Courbes */}
      {selectedSensors.length === 0 || allTimestamps.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Conteneur regroupé pour les deux courbes */}
          <div className='mb-4'>
            {/* Courbe Température/Humidité */}
            <div className='mb-2'>
              <h2 className='text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2 text-center'>
                Température & Humidité
              </h2>
              <div
                style={{
                  height: 300,
                  minWidth: 900,
                  maxWidth: '100vw',
                  overflowX: 'auto',
                  margin: '0 auto',
                  background: '#fff',
                  borderRadius: '8px 8px 0 0',
                  boxShadow: '0 2px 8px #0001',
                  padding: 16,
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
            <ResponsiveContainer width='100%' height={280}>
              <LineChart
                data={(() => {
                  // Appliquer le filtrage du TimeNavigator
                  const { startIndex, endIndex } = getFilteredIndices
                  const filteredTimestamps = allTimestamps.slice(startIndex, endIndex + 1)
                  
                  // On fusionne les données pour Recharts : chaque point = { date, temp_capteur1, hum_capteur1, temp_capteur2, ... }
                  return filteredTimestamps.map((timestamp: string, i: number) => {
                    const originalIndex = startIndex + i
                    const entry: any = {
                      date: new Date(timestamp).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }
                    temperatureChartData.datasets.forEach((ds: any) => {
                      entry[ds.label] = ds.data[originalIndex]
                    })
                    return entry
                  })
                })()
              }
              margin={{top: 20, right: 40, left: 10, bottom: 5}}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='date' minTickGap={20} tick={{fill: '#222', fontSize: 13}} hide />
                <YAxis
                  yAxisId='left'
                  label={{
                    value: 'Température (°C)',
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#2563eb',
                    fontSize: 13,
                  }}
                  tick={{fill: '#2563eb', fontSize: 13}}
                />
                <YAxis
                  yAxisId='right'
                  orientation='right'
                  label={{
                    value: 'Humidité (%)',
                    angle: 90,
                    position: 'insideRight',
                    fill: '#06b6d4',
                    fontSize: 13,
                  }}
                  tick={{fill: '#06b6d4', fontSize: 13}}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      // Filtrer les données min/max du tooltip
                      const filteredPayload = payload.filter((entry: any) => 
                        !entry.dataKey.includes('Min') && !entry.dataKey.includes('Max')
                      )
                      
                      if (filteredPayload.length === 0) return null
                      
                      return (
                        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                          <p className="font-medium mb-2">{`Date: ${label}`}</p>
                          {filteredPayload.map((entry: any, index: number) => (
                            <p key={index} style={{ color: entry.color }} className="text-sm">
                              {`${entry.dataKey}: ${entry.value}${entry.dataKey.includes('Température') ? '°C' : '%'}`}
                            </p>
                          ))}
                        </div>
                      )
                    }
                    return null
                  }}
                />
                 {temperatureChartData.datasets.map((ds: any, idx: number) => {
                  // Température
                  if (ds.label.includes('Température')) {
                    return (
                      <Line
                        key={ds.label}
                        yAxisId='left'
                        type='monotone'
                        dataKey={ds.label}
                        name={ds.label}
                        stroke={ds.borderColor}
                        dot={false}
                        strokeWidth={2}
                        hide={hiddenLines[ds.label]}
                      />
                    )
                  }
                  // Humidité
                  if (ds.label.includes('Humidité')) {
                    return (
                      <Line
                        key={ds.label}
                        yAxisId='right'
                        type='monotone'
                        dataKey={ds.label}
                        name={ds.label}
                        stroke={ds.borderColor}
                        dot={false}
                        strokeWidth={2}
                        hide={hiddenLines[ds.label]}
                      />
                    )
                  }
                  // Température Min/Max (ReferenceLine)
                  if (ds.label.includes('Température Min')) {
                    return (
                      <ReferenceLine
                        key={ds.label}
                        yAxisId='left'
                        y={ds.data[0]}
                        // label={{value: ds.label, fill: ds.borderColor, fontSize: 12}}
                        stroke={ds.borderColor}
                        strokeDasharray='5 5'
                      />
                    )
                  }
                  if (ds.label.includes('Température Max')) {
                    return (
                      <ReferenceLine
                        key={ds.label}
                        yAxisId='left'
                        y={ds.data[0]}
                        // label={{value: ds.label, fill: ds.borderColor, fontSize: 12}}
                        stroke={ds.borderColor}
                        strokeDasharray='5 5'
                      />
                    )
                  }
                  // Humidité Min/Max (ReferenceLine)
                  if (ds.label.includes('Humidité Min')) {
                    return (
                      <ReferenceLine
                        key={ds.label}
                        yAxisId='right'
                        y={ds.data[0]}
                        // label={{value: ds.label, fill: ds.borderColor, fontSize: 12}}
                        stroke={ds.borderColor}
                        strokeDasharray='3 3'
                      />
                    )
                  }
                  if (ds.label.includes('Humidité Max')) {
                    return (
                      <ReferenceLine
                        key={ds.label}
                        yAxisId='right'
                        y={ds.data[0]}
                        // label={{value: ds.label, fill: ds.borderColor, fontSize: 12}}
                        stroke={ds.borderColor}
                        strokeDasharray='3 3'
                      />
                    )
                  }
                  return null
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Custom Legend pour température/humidité */}
          {showLegends && (
            <div className='px-4 py-2 bg-gray-50 border-l border-r border-gray-200'>
              <CustomLegend
                datasets={temperatureChartData.datasets.filter(ds => 
                  !ds.label.includes('Min') && !ds.label.includes('Max')
                )}
                hiddenLines={hiddenLines}
                setHiddenLines={setHiddenLines}
              />
            </div>
          )}
        </div>

        {/* Courbe État de la porte */}
        <div className='mb-2'>
          <h2 className='text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2 text-center'>
            État de la porte
          </h2>
          <div
            style={{
              height: 180,
              minWidth: 900,
              maxWidth: '100vw',
              overflowX: 'auto',
              margin: '0 auto',
              background: '#fff',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 2px 8px #0001',
              padding: 16,
              borderTop: '1px solid #e5e7eb'
            }}
          >
            <ResponsiveContainer width='100%' height={160}>
              <LineChart
                data={(() => {
                  const fullData = allTimestamps.map((timestamp: string, i: number) => {
                    const entry: any = {
                      date: new Date(timestamp).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }
                    doorChartData.datasets.forEach((ds: any) => {
                      entry[ds.label] = ds.data[i]
                    })
                    return entry
                  })
                  
                  // Appliquer le zoom synchronisé si une plage est sélectionnée
                  const { startIndex, endIndex } = getFilteredIndices
                  if (startIndex !== undefined && endIndex !== undefined && startIndex < endIndex) {
                    return fullData.slice(startIndex, endIndex + 1)
                  }
                  return fullData
                })()
              }
              margin={{top: 20, right: 40, left: 10, bottom: 5}}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='date' minTickGap={20} tick={{fill: '#222', fontSize: 13}} />
                <YAxis
                  domain={[-0.1, 1.1]}
                  tickFormatter={(v: number) => (v === 1 ? 'Ouvert' : 'Fermé')}
                  ticks={[0, 1]}
                  tick={{fill: '#222', fontSize: 13}}
                />
                <Tooltip />
                {showLegends && <Legend />}
                {doorChartData.datasets.map((ds: any, idx: number) => (
                  <Line
                    key={ds.label}
                    type='stepAfter'
                    dataKey={ds.label}
                    name={ds.label}
                    stroke={ds.borderColor}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Composant de navigation temporelle ultra-fluide */}
        <TimeNavigator
          totalPoints={allTimestamps.length}
          timeRange={timeRange}
          onRangeChange={handleTimeRangeChange}
          timestamps={allTimestamps}
        />
      </div>
      </>
      )}

    </div>
  )
}

